import { TemplateResult, html, nothing, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import innerStyles from './outline-kyruus-provider-search-form.inner.pcss?inline';
import globalStyles from './outline-kyruus-provider-search-form.global.pcss?inline';
import { AdoptedStyleSheets } from '../../../controllers/adopted-stylesheets';

/**
 * The Main Provider Search Form Component
 * @element outline-kyruus-provider-search-form
 * @slot heading - Slot for the heading of the search form.
 * @slot description - Slot for the description of the search form.
 * @slot subheading - Slot for the subheading of the search form.
 * @slot form - Slot for the form of the search form.
 * @slot typeahead - Slot for the typeahead of the search form.
 * @property {Boolean} kyruusTypeahead - Property to enable or disable the typeahead feature.
 * @property {Boolean} showTypeahead - Property to show or hide the typeahead feature.
 * @property {Boolean} kyruusStandaloneSearchForm - Property to enable or disable the standalone search form.
 * @state kyruusStandaloneSearchFormActionBase - State to hold the base action URL for the standalone search form.
 */

@customElement('outline-kyruus-provider-search-form')
export class OutlineKyruusProviderSearchForm extends LitElement {
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    globalCSS: globalStyles,
    encapsulatedCSS: innerStyles,
  });

  @property({ type: Boolean, reflect: true, attribute: 'kyruus-typeahead' })
  kyruusTypeahead = false;

  @property({ type: Boolean, reflect: true, attribute: 'show-typeahead' })
  showTypeahead = false;

  @property({
    type: Boolean,
    reflect: true,
    attribute: 'kyruus-standalone-search-form',
  })
  kyruusStandaloneSearchForm = false;

  @state() kyruusStandaloneSearchFormActionBase: string;

  // Holds a reference to the timeout ID for the typeahead functionality, or null if no timeout is set.
  private typeaheadTimeout: number | null = null;

  // Handles input events for the typeahead feature on the input element with id 'keyword'.
  private onTypeaheadInput(event: Event): void {
    // Hide the typeahead when someone types.
    this.showTypeahead = false;
    // Cast the event target to an HTMLInputElement.
    const inputElement = event.target as HTMLInputElement;
    // Check if the input element exists and has the correct id.
    if (inputElement && inputElement.id === 'keyword') {
      // If a timeout is already set, clear it to prevent multiple requests.
      if (this.typeaheadTimeout !== null) {
        clearTimeout(this.typeaheadTimeout);
      }
      // Get the current value of the input field.
      const terms = inputElement.value;
      // If the input value is at least 2 characters long, proceed with typeahead.
      if (terms.length >= 2) {
        // Set a timeout to delay the typeahead request, avoiding too many requests during typing.
        this.typeaheadTimeout = window.setTimeout(() => {
          // Fetch the typeahead suggestions from the server using the input value.
          fetch(`/api/kyruus/typeahead?terms=${encodeURIComponent(terms)}`)
            // Parse the response as text.
            .then(response => response.text())
            // Use the response to render the typeahead suggestions.
            .then(html => {
              this.renderTypeahead(html);
              this.showTypeahead = true;
            });
        }, 500); // The delay is set to 500 milliseconds.
      }
    }
  }

  /**
   * Renders the typeahead suggestions by updating the inner HTML of the typeahead slot.
   * It also attaches click event listeners to buttons within the typeahead suggestions
   * to handle selection of a suggestion.
   *
   * @param html - The HTML string containing typeahead suggestions to be rendered.
   */
  private renderTypeahead(html: string): void {
    // Find the DOM element designated as the typeahead slot.
    const typeaheadSlot = this.querySelector('[slot="typeahead"]');

    // If the typeahead slot is found, update its content and attach event listeners.
    if (typeaheadSlot) {
      // Update the inner HTML of the typeahead slot with the provided HTML string.
      typeaheadSlot.innerHTML = html;

      // Initialize htmx on the new content. Note we don't want to create an
      // htmx dependency here, so we'll first test if it is available, and then
      // execute if it is.
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (window.htmx) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).htmx.process(typeaheadSlot);
      }

      // Find all buttons within the typeahead slot that are used for typeahead suggestions.
      const typeaheadButtons =
        typeaheadSlot.querySelectorAll('.typeahead-button');

      // Attach a click event listener to each button to handle typeahead suggestion selection.
      typeaheadButtons.forEach(button => {
        button.addEventListener(
          'click',
          this.handleTypeaheadButtonClick.bind(this)
        );
      });

      // Query the DOM for the close button within the typeahead slot
      const typeaheadCloseButton =
        typeaheadSlot.querySelector('.typeahead-close');
      // If the close button is found, attach a click event listener to it
      if (typeaheadCloseButton) {
        // The event listener calls handleTypeaheadCloseButton when the button is clicked
        typeaheadCloseButton.addEventListener('click', () =>
          this.handleTypeaheadCloseButton()
        );
      }
    }
  }

  /**
   * Handles click events on typeahead suggestion buttons by updating the search input
   * with the selected suggestion's value and triggering the search form submission.
   *
   * @param event - The click event object from the typeahead suggestion button.
   */
  private handleTypeaheadButtonClick(event: Event): void {
    // Get the button element that was clicked.
    const button = event.currentTarget as HTMLButtonElement;

    // Retrieve the search value associated with the clicked button.
    const searchValue = button.getAttribute('data-search-value');

    // Find the search input element by its ID.
    const inputElement = this.querySelector(
      'input#keyword'
    ) as HTMLInputElement;

    // Find the closest form element that contains the search input.
    const formElement = inputElement.closest('form') as HTMLFormElement;

    // If both the input element and search value are found, update the input and submit the form.
    if (inputElement && searchValue) {
      // Set the value of the search input to the selected suggestion's value.
      inputElement.value = searchValue;

      // Find the submit button within the form and trigger a click to submit the form.
      const submitButton = formElement.querySelector(
        'button[type="submit"]'
      ) as HTMLButtonElement;
      submitButton.click();
    }
  }

  /**
   * Handles the closing of the typeahead suggestions list.
   * When invoked, it sets the `showTypeahead` property to false,
   * which is expected to hide the typeahead suggestions from the view.
   */
  private handleTypeaheadCloseButton(): void {
    this.showTypeahead = false;
  }

  private onKyruusStandaloneSearchInput(formElement: HTMLFormElement): void {
    // Make sure we have an input element, if not bail.
    const keywordInputElement = this.querySelector(
      'input#keyword'
    ) as HTMLInputElement | null;
    if (!keywordInputElement) {
      console.error('Keyword input element not found.');
      return;
    }

    // Get our keyword input value
    const keywordValue = keywordInputElement.value;

    // URI encode it because we're going to use it
    const encodedKeywordValue = encodeURIComponent(keywordValue);

    formElement.action =
      this.kyruusStandaloneSearchFormActionBase + encodedKeywordValue;
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.kyruusTypeahead) {
      this.onTypeaheadInput = this.onTypeaheadInput.bind(this);
    }
  }

  disconnectedCallback() {
    const inputElement = this.querySelector('input#keyword');
    inputElement?.removeEventListener(
      'input',
      this.onTypeaheadInput.bind(this)
    );
  }

  firstUpdated() {
    if (this.kyruusTypeahead) {
      const inputElement = this.querySelector('input#keyword');
      inputElement?.addEventListener('input', this.onTypeaheadInput.bind(this));
    }

    if (this.kyruusStandaloneSearchForm) {
      // Make sure we've got a form element, if not bail.
      const formElement = this.querySelector('form') as HTMLFormElement | null;

      if (!formElement) {
        console.error('Form element not found');
        return;
      }
      this.kyruusStandaloneSearchFormActionBase = formElement.action;
      const inputElement = this.querySelector('input#keyword');
      inputElement?.addEventListener('input', () =>
        this.onKyruusStandaloneSearchInput(formElement)
      );
    }
  }

  slotTemplate(slotName: string) {
    if (!this.querySelector(`[slot=${slotName}]`)) {
      return nothing;
    } else {
      return html`<slot name="${slotName}"></slot>`;
    }
  }

  render(): TemplateResult {
    return html`
      <div class="content-">
        ${this.slotTemplate('heading')} ${this.slotTemplate('description')}
        ${this.querySelector(`[slot='form']`)
          ? html` <div class="form-wrapper">
              ${this.slotTemplate('subheading')} ${this.slotTemplate('form')}
              ${this.kyruusTypeahead
                ? html`<div class="typeahead">
                    <slot name="typeahead"></slot>
                  </div>`
                : nothing}
            </div>`
          : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'outline-kyruus-provider-search-form': OutlineKyruusProviderSearchForm;
  }
}
